import React, { useCallback } from "react";

import {
  formSectionPropTypes,
  FormPageSection,
} from "../../../layout/FormPageSection";
import { createTeamFields } from "./createTeamFields";
import { useSelector } from "react-redux";
import { consultingDCId } from "../../../inputs/inputConfigs/projects";
import { getBluechipResources } from "../../../../utils/bluechipUtils";
import { getDataComponentFlattenedRequestState } from "../../../../reducers/dataComponentReducer";

const projectManagerType = "Project Manager";
const invoiceSubmissionsType = "Invoice Submissions";

const memberTypes = {
  freightVendorId: "Freight Vendor",
  warehouseVendorId: "Warehouse Vendor",
  projectManagerId: "Project Manager",
  secondaryProjectManagerId: "Secondary Project Manager",
  designerId: "Interior Designer",
  secondaryDesignerId: "Secondary Interior Designer",
  generalContractorId: "General Contractor",
  otherConsultantId: "Other",
};

const isContactInvoiceProjectManager = (contact, memberContacts) =>
  contact.memberType === invoiceSubmissionsType &&
  memberContacts.some(
    user =>
      user.memberType === projectManagerType &&
      user.contactId === contact.contactId &&
      user.name === contact.name
  );

const getInvoiceSubmissionContacts = (memberType, values, memberContacts) =>
  memberType === "projectManagerId" ||
  memberType === "secondaryProjectManagerId"
    ? values.projectMemberContacts.filter(contact =>
        isContactInvoiceProjectManager(contact, memberContacts)
      )
    : [];

const filterProjectManagersAndInvoiceSubmissions = (
  memberType,
  contact,
  invoiceSubmissionsContacts
) =>
  contact.memberType !== memberTypes[memberType] &&
  !invoiceSubmissionsContacts.some(
    user => user.contactId === contact.contactId && user.name === contact.name
  );

const getMembersFromProjectCompany = (projectManagerId, consultingCompanies) =>
  consultingCompanies
    .find(
      company =>
        company.type === projectManagerType && company.id === projectManagerId
    )
    ?.consultingCompanyContacts?.map(member => ({
      contactId: member.contactId,
      name: member.contact.name,
      memberType: projectManagerType,
    })) ?? [];

const TeamSectionForm = props => {
  const {
    project,
    values,
    touched,
    setFieldValue,
    openModalDialog,
    ...rest
  } = props;

  const state = useSelector(state => state);
  const dataComponent = getDataComponentFlattenedRequestState(
    consultingDCId,
    state
  );

  const consultingCompanies = getBluechipResources(dataComponent, state) || [];

  const syncProjectMemberContacts = useCallback(
    ({ target: { name, value } }) => {
      const clearRelatedContacts = () => {
        const primaryMemberContacts = getMembersFromProjectCompany(
          project.projectManagerId,
          consultingCompanies
        );
        const secondaryMemberContacts = getMembersFromProjectCompany(
          project.secondaryProjectManagerId,
          consultingCompanies
        );
        const memberContacts = [
          ...primaryMemberContacts,
          ...secondaryMemberContacts,
        ];
        const invoiceSubmissionsContacts = getInvoiceSubmissionContacts(
          name,
          values,
          memberContacts
        );

        setFieldValue(
          "projectMemberContacts",
          values.projectMemberContacts.filter(contact =>
            filterProjectManagersAndInvoiceSubmissions(
              name,
              contact,
              invoiceSubmissionsContacts
            )
          )
        );
      };

      const originalDesignerId = project[name];
      const newDesignerId = value || null;
      const isReplacing = !!newDesignerId;

      const onConfirm = () => {
        setFieldValue(name, newDesignerId);
        clearRelatedContacts();
      };

      if (
        ["designerId", "secondaryDesignerId"].includes(name) &&
        !!originalDesignerId
      ) {
        openModalDialog(
          isReplacing ? "Replace Designer" : "Remove Designer",
          "DesignerConfirmationModal",
          {
            isReplacing,
            projectId: project.id,
            originalDesignerId,
            newDesignerId,
            onConfirm,
          },
          true,
          false
        );
        return true;
      }
      clearRelatedContacts();
    },
    [openModalDialog, project, setFieldValue, values, consultingCompanies]
  );

  const fields = createTeamFields(values, syncProjectMemberContacts);

  return (
    <FormPageSection
      headerText="Teams"
      isPage={true}
      fields={fields}
      {...rest}
    />
  );
};
TeamSectionForm.propTypes = formSectionPropTypes;

export default TeamSectionForm;
